
import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useGoServerStore } from '../stores/goServerStore';

const POLLING_INTERVAL = 10 * 60 * 1000; // 10 minutes

export const useSystemUpdate = () => {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [remoteVersion, setRemoteVersion] = useState<string>('');
    const { connectionStatus, fetchMetaVersion } = useGoServerStore();

    const checkUpdates = async () => {
        // Don't check when the local audio server is offline
        if (!useGoServerStore.getState().connectionStatus.isConnected) {
            setIsUpdateAvailable(false);
            return;
        }

        try {
            // 1. Get Local Version from the store (fetched via SDK → localhost:8080).
            //    The previous approach used fetch('/meta/version') which is a relative URL
            //    and breaks in production where the frontend is served from a different origin.
            await fetchMetaVersion();
            const localVersion = useGoServerStore.getState().metaStatus.version;
            if (!localVersion) return;

            // 2. Get Remote Version (from Backend)
            const remoteDataRes = await api.get('/audio-server/update-info');
            const remoteData = remoteDataRes.data;
            const remoteVer = remoteData.version;

            // 3. Compare
            if (compareVersions(remoteVer, localVersion) > 0) {
                setIsUpdateAvailable(true);
                setRemoteVersion(remoteVer);
            }
        } catch (error) {
            console.error('Failed to check for system updates:', error);
        }
    };

    useEffect(() => {
        checkUpdates();
        const interval = setInterval(checkUpdates, POLLING_INTERVAL);
        return () => clearInterval(interval);
    }, []);

    // Re-check when connection comes up, hide when it goes down
    useEffect(() => {
        if (connectionStatus.isConnected) {
            checkUpdates();
        } else {
            setIsUpdateAvailable(false);
        }
    }, [connectionStatus.isConnected]);

    return { isUpdateAvailable, remoteVersion };
};

// Simple semantic version comparison
// Returns 1 if v1 > v2, -1 if v1 < v2, 0 if equal
function compareVersions(v1: string, v2: string): number {
    const p1 = v1.split('.').map(Number);
    const p2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
        const n1 = p1[i] || 0;
        const n2 = p2[i] || 0;
        if (n1 > n2) return 1;
        if (n1 < n2) return -1;
    }
    return 0;
}
